import { useEffect, useRef } from 'react';
import { tss } from 'tss-react/mui';

const useStyles = tss.create({
  iframe: {
    border: '0',
    minHeight: 200,
    width: '100%',
  },
});

type Props = {
  iframeTitle: string;
  url: string;
};

export const FullSizeIFrame = ({ iframeTitle, url }: Props) => {
  const { classes } = useStyles();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const scrollAreaRef = useRef<HTMLElement>();

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) {
      return () => void 0;
    }

    const onResize = () => {
      if (scrollAreaRef.current && iframeRef.current) {
        iframeRef.current.height = `${scrollAreaRef.current.scrollHeight}`;
      }
    };
    // ResizeObserver is used to resize the iframe to fit the content
    // of the page inside it.
    const resizeObserver = new ResizeObserver(onResize);
    // MutationObserver is needed because the page-scrollbar element is lazy
    // rendered by  so we need to wait for it to be added to the DOM
    // before we can start observing it.
    const mutationObserver = new MutationObserver(() => {
      const scrollContainer =
        iframe.contentDocument?.querySelector<HTMLDivElement>(
          '[class$="-page-wrapper"] > div',
        );
      if (!scrollContainer) {
        return;
      }
      scrollAreaRef.current = scrollContainer;

      onResize();
      resizeObserver.observe(scrollContainer);
    });

    const onLoad = () => {
      if (iframe.contentDocument?.body) {
        mutationObserver.observe(iframe.contentDocument.body, {
          childList: true,
          subtree: true,
        });
      }
    };

    iframe.addEventListener('load', onLoad);

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
      iframe.removeEventListener('load', onLoad);
    };
  }, []);

  return (
    <iframe
      className={classes.iframe}
      data-testid="grafana-iframe"
      ref={iframeRef}
      src={url}
      title={iframeTitle}
    />
  );
};
